import { Stack, Typography } from "@mui/material";
import { formatDistanceToNowStrict } from "date-fns";

import { FilepickerFile } from "@/components/ui-v2/FilePicker/types";
import {
  AudioPreviewTile,
  AudioPreviewTileAction,
} from "@/components/ui-v2/Tiles/AudioPreviewTile/AudioPreviewTile";
import { TileObjectTypeIndicator } from "@/components/ui-v2/Tiles/common/TileObjectTypeIndicator";
import {
  ImagePreviewTile,
  ImagePreviewTileAction,
} from "@/components/ui-v2/Tiles/ImagePreviewTile/ImagePreviewTile";
import {
  VideoPreviewTile,
  VideoPreviewTileAction,
} from "@/components/ui-v2/Tiles/VideoPreviewTile/VideoPreviewTile";

export type RecentTileAction =
  | VideoPreviewTileAction
  | AudioPreviewTileAction
  | ImagePreviewTileAction;

interface RecentTileProps {
  file: FilepickerFile;
  isSelected: boolean;
  onAction: (
    event: React.MouseEvent,
    action: RecentTileAction,
    item: FilepickerFile,
  ) => void;
}

export const RecentTile = (props: RecentTileProps) => {
  const { file, isSelected, onAction } = props;
  const fileSubtext = `Used ${formatDistanceToNowStrict(file.interactedAt)} ago`;
  const { type } = file;

  const handleAction = (
    event: React.MouseEvent,
    action:
      | VideoPreviewTileAction
      | AudioPreviewTileAction
      | ImagePreviewTileAction,
  ) => {
    onAction(event, action, file);
  };

  const commonProps = {
    isSelected,
    isSelectable: true,
    slots: {
      topLeftEl: TileObjectTypeIndicator,
    },
    slotProps: {
      topLeftEl: {
        objectType: type,
        isSelected,
      },
    },
  };

  let tile: React.ReactNode | null = null;

  if (type === "image") {
    tile = (
      <ImagePreviewTile
        {...commonProps}
        src={file.thumbnailSrc}
        alt={file.name}
        onAction={handleAction}
      />
    );
  } else if (type === "video") {
    tile = (
      <VideoPreviewTile
        {...commonProps}
        slots={{
          ...commonProps.slots,
          topRightEl: undefined,
        }}
        isLazyloaded
        shouldPlayOnSelected
        shouldResetOnStop
        src={file.src}
        onAction={handleAction}
      />
    );
  } else if (type === "audio") {
    tile = (
      <AudioPreviewTile
        {...commonProps}
        url={file.src}
        onAction={handleAction}
      />
    );
  }

  if (!tile) {
    return null;
  }

  return (
    <Stack spacing={1.5}>
      {tile}
      <Stack>
        <Typography
          noWrap
          variant="micro"
          sx={{
            fontWeight: "bold",
          }}
        >
          {file.name}
        </Typography>
        <Typography
          variant="micro"
          sx={{
            color: "secondary.2",
          }}
        >
          {fileSubtext}
        </Typography>
      </Stack>
    </Stack>
  );
};
